import PropTypes from "prop-types";
import { Check } from "lucide-react";
import { LETTERS, TILE_CLASS } from "./game";

/**
 * The answer tiles: a coloured block per choice with its letter and text.
 * On the projector they're read-only; on a phone they're buttons
 * (`onPick`). After the reveal, `correct` lights the right one, mutes the
 * rest, and `counts` says how many picked each. Once a phone has picked,
 * the others mute too. Muting swaps the fill for the muted surface, never
 * opacity: a faded tile took its text and counts to 1.8:1 (review of #451).
 */
function ChoiceTiles({
  choices,
  correct = null,
  counts = null,
  picked = null,
  onPick = null,
  disabled = false,
  big = false,
}) {
  const revealed = correct !== null;
  return (
    <ol className={`grid gap-3 ${big ? "md:grid-cols-2 md:gap-4" : ""}`} aria-label="Choices">
      {choices.map((text, i) => {
        const letter = LETTERS[i];
        const isRight = revealed && letter === correct;
        const dim = revealed ? !isRight : picked !== null && picked !== letter;
        const content = (
          <>
            <span
              className={`flex shrink-0 items-center justify-center rounded-md font-semibold ${
                dim ? "bg-background" : "bg-trivia-on/20"
              } ${
                big ? "size-12 text-2xl" : "size-9 text-lg"
              }`}
              aria-hidden="true"
            >
              {letter}
            </span>
            <span className={`min-w-0 flex-1 text-left font-medium ${big ? "text-2xl lg:text-3xl" : "text-lg"}`}>
              <span className="sr-only">{letter}:</span> {text}
            </span>
            {isRight && <Check className={big ? "size-9" : "size-7"} aria-label="Right answer" />}
            {counts && (
              <span className={`shrink-0 tabular-nums ${big ? "text-2xl" : "text-base"}`}>
                {counts[i] ?? 0}
                <span className="sr-only"> picked this</span>
              </span>
            )}
          </>
        );
        const tileClass = `flex w-full items-center gap-3 rounded-lg p-3 transition-colors ${
          dim ? "border bg-muted text-foreground" : `${TILE_CLASS[letter]} text-trivia-on`
        } ${big ? "min-h-20 p-4" : "min-h-16"} ${
          picked === letter ? "ring-4 ring-foreground ring-offset-2 ring-offset-background" : ""
        }`;
        return (
          <li key={letter}>
            {onPick ? (
              <button
                type="button"
                className={`${tileClass} disabled:cursor-default`}
                onClick={() => onPick(letter)}
                disabled={disabled}
                aria-pressed={picked === letter}
                data-dim={dim || undefined}
              >
                {content}
              </button>
            ) : (
              <div className={tileClass} data-dim={dim || undefined}>
                {content}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

ChoiceTiles.propTypes = {
  choices: PropTypes.arrayOf(PropTypes.string).isRequired,
  correct: PropTypes.string,
  counts: PropTypes.arrayOf(PropTypes.number),
  picked: PropTypes.string,
  onPick: PropTypes.func,
  disabled: PropTypes.bool,
  big: PropTypes.bool,
};

export default ChoiceTiles;
